/**
 * The Field Manual — the how-to-play modal.
 *
 * A landing-only surface: a button opens a tabbed briefing (Getting started /
 * Civilian / Authority) with gameplay footage and the real mechanics, and its
 * footer drops you straight onto a side. Everything guards on the elements
 * existing, so calling this in solo or admin mode (where the landing is hidden)
 * does nothing rather than throwing.
 */
export function initGuide(): void {
  const modal = document.getElementById('guide-modal');
  const open = document.getElementById('guide-open');
  if (!modal || !open) return;

  const underline = document.getElementById('gm-underline');
  const tabs = [...modal.querySelectorAll<HTMLButtonElement>('.gm-tab')];
  const panes = [...modal.querySelectorAll<HTMLElement>('.gm-pane')];
  let lastFocus: HTMLElement | null = null;

  const moveUnderline = (tab: HTMLElement): void => {
    if (!underline) return;
    underline.style.width = `${tab.offsetWidth}px`;
    underline.style.transform = `translateX(${tab.offsetLeft}px)`;
  };

  const select = (key: string): void => {
    for (const tab of tabs) {
      const on = tab.dataset.gmTab === key;
      tab.classList.toggle('is-on', on);
      tab.setAttribute('aria-selected', String(on));
      if (on) moveUnderline(tab);
    }
    for (const pane of panes) {
      const on = pane.dataset.gmPane === key;
      pane.hidden = !on;
      pane.classList.toggle('is-on', on);
      // Only the visible pane's footage should be playing.
      const video = pane.querySelector('video');
      if (video) {
        if (on) {
          video.currentTime = 0;
          void video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    }
  };

  const show = (): void => {
    lastFocus = document.activeElement as HTMLElement | null;
    modal.hidden = false;
    // Kick the visible pane's footage playing (autoplay can be held while hidden).
    const activePane = panes.find((pane) => pane.classList.contains('is-on')) ?? panes[0];
    const video = activePane?.querySelector('video');
    if (video) void video.play().catch(() => {});
    // The underline needs a laid-out tab to measure, so place it next frame.
    requestAnimationFrame(() => {
      const active = tabs.find((t) => t.classList.contains('is-on')) ?? tabs[0];
      if (active) moveUnderline(active);
    });
    document.getElementById('gm-close')?.focus();
  };

  const hide = (): void => {
    modal.hidden = true;
    for (const video of modal.querySelectorAll('video')) video.pause();
    lastFocus?.focus();
  };

  open.addEventListener('click', show);
  document.getElementById('gm-close')?.addEventListener('click', hide);
  document.getElementById('gm-backdrop')?.addEventListener('click', hide);

  for (const tab of tabs) {
    tab.addEventListener('click', () => {
      if (tab.dataset.gmTab) select(tab.dataset.gmTab);
    });
  }

  modal.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      hide();
      return;
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      const at = tabs.findIndex((t) => t.classList.contains('is-on'));
      const step = event.key === 'ArrowRight' ? 1 : tabs.length - 1;
      const next = tabs[(at + step) % tabs.length];
      if (next?.dataset.gmTab) {
        select(next.dataset.gmTab);
        next.focus();
      }
    }
  });

  // "Play as ..." in the footer: close the manual and take the matching door.
  for (const go of modal.querySelectorAll<HTMLButtonElement>('.gm-go')) {
    go.addEventListener('click', () => {
      hide();
      const id = go.dataset.gmGo === 'authority' ? 'landing-authority' : 'landing-drive';
      document.getElementById(id)?.click();
    });
  }
}
